import { useState, useMemo, useEffect, useCallback } from 'react';
import { useFormik, FormikProvider } from 'formik';
import { useDispatch } from 'react-redux';
import { Box, Alert, CircularProgress } from '@mui/material';
import { FormikStepperContext } from '../contexts/FormikStepperContext';
import {
    SendLeaseForm,
    AttachPropertyForms,
    PreviewLeaseByManager,
    SignLeaseByTenants,
    SetLeaseAsActive
} from '../../components/steps';
import { lease_modes_allowed, validators } from '../../components/config';
import { useLeaseInitialValues, useLeaseStepSubmission } from '../../components/hooks';
import { SET_FORM_DATA } from '../../../../../../../../../store/leaseForm/actionTypes';
import { useDebounce } from '../../../sign-lease/utils';

export const FormikProviderWrapper = ({ children, mode }) => {
    const [error, setError] = useState('');
    const [currentStep, setCurrentStep] = useState(0);
    const [isOnPreviousTab, setIsOnPreviousTab] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const dispatch = useDispatch();

    const setCurrentStepIfAllowed = useCallback((stepIndex) => {
        if (!isOnPreviousTab) {
            setCurrentStep(stepIndex);
        }
    }, [isOnPreviousTab]);

    const {
        initialValues,
        loading,
        initialMode,
        leaseId,
    } = useLeaseInitialValues({
        currentStep,
        setCurrentStep: setCurrentStepIfAllowed,
        isOnPreviousTab,
        setError
    });

    const modeLabel = initialMode ? lease_modes_allowed[initialMode] || 'Lease' : 'Lease';

    const handleNext = useCallback(() => {
        setCurrentStep((prev) => prev + 1);
    }, []);

    const leaseSteps = useMemo(() => [
        {
            label: `1 - ${initialMode ? `Send ${modeLabel}` : 'Send Lease'} By Manager`,
            component: SendLeaseForm,
            stepKey: 'step1',
        },
        {
            label: '2 - Attach Property Forms',
            component: AttachPropertyForms,
            stepKey: 'step2',
        },
        {
            label: `3 - Preview ${initialMode ? modeLabel : 'Lease'} By Manager`,
            component: PreviewLeaseByManager,
            stepKey: 'step3',
        },
        {
            label: `4 - Sign ${initialMode ? modeLabel : 'Lease'} By Tenant`,
            component: SignLeaseByTenants,
            stepKey: 'step4',
        },
        {
            label: `5 - Set ${initialMode ? modeLabel : 'Lease'} As Active`,
            component: SetLeaseAsActive,
            stepKey: 'step5',
        }
    ], [initialMode, modeLabel]);

    const stepKey = currentStep >= 0 && currentStep < leaseSteps.length
        ? leaseSteps[currentStep].stepKey
        : 'step1';

    const formik = useFormik({
        initialValues: initialValues || {},
        onSubmit: (values, { setSubmitting }) => {
            const submitHandler = getSubmitHandler(stepKey);
            if (submitHandler) {
                submitHandler(values).then((success) => {
                    setSubmitting(false);
                    if (success) handleNext();
                });
            } else {
                setSubmitting(false);
            }
        },
        validateOnBlur: false,
        validateOnChange: false,
        enableReinitialize: true,
    });

    const { getSubmitHandler } = useLeaseStepSubmission({
        validators,
        setIsSubmitting: formik.setSubmitting,
        setError,
        handleNext,
        leaseId
    });

    const areValuesDifferent = useCallback((current, initial, ignoreKeys = []) => {
        const filteredCurrent = { ...current };
        const filteredInitial = { ...initial };
        ignoreKeys.forEach((key) => {
            delete filteredCurrent[key];
            delete filteredInitial[key];
        });
        return JSON.stringify(filteredCurrent) !== JSON.stringify(filteredInitial);
    }, []);

    const debouncedValues = useDebounce(formik.values, 1500);

    useEffect(() => {
        if (areValuesDifferent(debouncedValues, initialValues, ['property_details', 'manager'])) {
            dispatch({ type: SET_FORM_DATA, payload: debouncedValues });
        }
    }, [debouncedValues, dispatch]);

    const nextStep = useCallback(() => {
        setCurrentStep((prev) => Math.min(prev + 1, leaseSteps.length - 1));
    }, [leaseSteps.length]);

    const prevStep = useCallback(() => {
        setIsOnPreviousTab(true);
        setCurrentStep((prev) => Math.max(prev - 1, 0));
    }, []);

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
                <CircularProgress />
            </Box>
        );
    }

    if (mode && !initialMode) {
        return (
            <Box sx={{ p: 3 }}>
                <Alert severity="error">
                    {`Invalid lease mode: ${mode}. Please use a valid mode: ${Object.keys(lease_modes_allowed).join(', ')}`}
                </Alert>
            </Box>
        );
    }

    const formikStepperContextValue = {
        leaseSteps,
        leaseId,
        currentStep,
        setCurrentStep,
        nextStep,
        prevStep,
        isSubmitting,
        setIsSubmitting,
        isOnPreviousTab,
        setIsOnPreviousTab,
        error,
        setError
    };

    return (
        <FormikStepperContext.Provider value={formikStepperContextValue}>
            <FormikProvider value={formik}>
                {children}
            </FormikProvider>
        </FormikStepperContext.Provider>
    );
};